import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';
import { UserProfile } from './useUserProfile';

/**
 * Normalize avatar URL - ensure it's a full URL if it's a path
 */
export function normalizeAvatarUrl(avatarUrl: string | null | undefined): string | null {
  if (!avatarUrl) return null;
  const cleanAvatarUrl = avatarUrl.trim();
  if (!cleanAvatarUrl) return null;
  if (cleanAvatarUrl.startsWith('http://')) {
    return cleanAvatarUrl.replace('http://', 'https://');
  }
  if (cleanAvatarUrl.startsWith('https://')) {
    return cleanAvatarUrl;
  }
  if (cleanAvatarUrl.startsWith('//')) {
    return `https:${cleanAvatarUrl}`;
  }
  // If it's a path (e.g., "userId/avatar.jpg"), get the public URL
  const { data: { publicUrl } } = supabase.storage
    .from('avatars')
    .getPublicUrl(cleanAvatarUrl.replace(/^\/+/, ''));
  return publicUrl;
}

/**
 * Hook to fetch user profile by email
 * Uses React Query for caching to prevent image loss when navigating
 */
export function useUserProfileByEmail(email: string | null) {
  return useQuery({
    queryKey: ['userProfileByEmail', email],
    queryFn: async (): Promise<UserProfile | null> => {
      if (!email) return null;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('email', email)
          .single();

        if (error) {
          // Don't log error if profile not found (user might not have profile yet)
          if (error.code !== 'PGRST116') {
            logger.error('Error fetching user profile by email:', error);
          }
          return null;
        }

        if (data) {
          const profile: UserProfile = {
            ...data,
            avatar_url: normalizeAvatarUrl(data.avatar_url),
          };

          return profile;
        }

        return null;
      } catch (err) {
        logger.error('Exception fetching user profile by email:', err);
        return null;
      }
    },
    enabled: !!email,
    staleTime: 2 * 60 * 60 * 1000, // 2 hours - avatares rara vez cambian
    gcTime: 3 * 60 * 60 * 1000, // 3 hours - mantener en cache aún más tiempo
    retry: 1,
    refetchOnWindowFocus: false, // Don't refetch on window focus - use cache
    refetchOnMount: false, // Don't refetch on mount if data is fresh
  });
}

/**
 * Hook to fetch user profile by ID
 * Uses React Query for caching to prevent image loss when navigating
 */
export function useUserProfileById(userId: string | null) {
  return useQuery({
    queryKey: ['userProfileById', userId],
    queryFn: async (): Promise<UserProfile | null> => {
      if (!userId) return null;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          if (error.code !== 'PGRST116') {
            logger.error('Error fetching user profile by ID:', error);
          }
          return null;
        }

        if (data) {
          const profile: UserProfile = {
            ...data,
            avatar_url: normalizeAvatarUrl(data.avatar_url),
          };

          return profile;
        }

        return null;
      } catch (err) {
        logger.error('Exception fetching user profile by ID:', err);
        return null;
      }
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 60 * 1000, // 2 hours - avatares rara vez cambian
    gcTime: 3 * 60 * 60 * 1000, // 3 hours - mantener en cache aún más tiempo
    retry: 1,
    refetchOnWindowFocus: false, // Don't refetch on window focus - use cache
    refetchOnMount: false, // Don't refetch on mount if data is fresh
  });
}

/**
 * Hook to fetch multiple user profiles by IDs
 * Useful for chat where we need to show avatars for multiple users
 */
export function useUserProfilesByIds(userIds: string[]) {
  // Sort and deduplicate IDs for consistent cache key
  const sortedUniqueIds = useMemo(() => {
    return Array.from(new Set(userIds)).sort();
  }, [userIds]);

  return useQuery({
    queryKey: ['userProfilesByIds', sortedUniqueIds.join(',')],
    queryFn: async (): Promise<Map<string, UserProfile>> => {
      if (sortedUniqueIds.length === 0) return new Map();

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .in('id', sortedUniqueIds);

        if (error) {
          logger.error('Error fetching user profiles by IDs:', error);
          return new Map();
        }

        const profilesMap = new Map<string, UserProfile>();
        (data || []).forEach((profile) => {
          profilesMap.set(profile.id, {
            ...profile,
            avatar_url: normalizeAvatarUrl(profile.avatar_url),
          });
        });

        logger.log(`✅ Fetched ${profilesMap.size} user profiles by IDs (cached)`);
        return profilesMap;
      } catch (err) {
        logger.error('Exception fetching user profiles by IDs:', err);
        return new Map();
      }
    },
    enabled: sortedUniqueIds.length > 0,
    staleTime: 2 * 60 * 60 * 1000, // 2 hours - avatares rara vez cambian
    gcTime: 3 * 60 * 60 * 1000, // 3 hours - mantener en cache aún más tiempo
    retry: 1,
    refetchOnWindowFocus: false, // Don't refetch on window focus - use cache
    refetchOnMount: false, // Don't refetch on mount if data is fresh
  });
}

